import React, { useMemo } from 'react';
import { Card, DatePicker, Divider, Select, Space, Tooltip } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FilterBar } from '../../../components/list';
import { useUserSegmentScope } from '../../auth/hooks/useUserSegmentScope';
import { usePermission } from '../../permissions/PermissionsContext';
import { useCustomerSegmentOptions } from '../../../hooks/options/useCustomerSegmentOptions';
import { useContactCategories } from '../../contacts/hooks/useContactCategories';
import { useUserOptions } from '../../../hooks/options/useUserOptions';
import { defaultDailyTrackingDateRange } from '../api/dailyTaskTracking';

dayjs.extend(utc);

const { RangePicker } = DatePicker;

export interface DailyTaskTrackingFilterState {
  dateRange: [Dayjs, Dayjs];
  segmentIds: string[];
  contactCategoryIds: number[];
  assigneeIds: string[];
}

interface Props {
  value: DailyTaskTrackingFilterState;
  onChange: (value: DailyTaskTrackingFilterState) => void;
}

const DailyTaskTrackingFilterBar: React.FC<Props> = ({ value, onChange }) => {
  const { isScoped, assignedSegmentIds } = useUserSegmentScope();
  const { data: segments = [] } = useCustomerSegmentOptions({ activeOnly: true });
  const { data: categoriesData } = useContactCategories();
  const { data: users = [] } = useUserOptions();
  const canManage = usePermission('administrator.access');

  const segmentOptions = useMemo(() => {
    const all = segments.map((s) => ({ value: s.id, label: s.label }));
    if (!isScoped) {
      return all;
    }
    const allowed = new Set(assignedSegmentIds);
    return all.filter((o) => allowed.has(o.value));
  }, [segments, isScoped, assignedSegmentIds]);

  const categoryOptions = useMemo(
    () =>
      (categoriesData ?? [])
        .filter((c) => c.isActive)
        .map((c) => ({ value: c.id, label: c.label })),
    [categoriesData]
  );

  const assigneeOptions = useMemo(
    () => users.map((u) => ({ value: u.id, label: u.label })),
    [users]
  );

  const defaultRange = defaultDailyTrackingDateRange();
  const hasFilters =
    value.segmentIds.length > 0 ||
    value.contactCategoryIds.length > 0 ||
    value.assigneeIds.length > 0 ||
    !value.dateRange[0].isSame(defaultRange[0], 'day') ||
    !value.dateRange[1].isSame(defaultRange[1], 'day');

  return (
    <Card size="small" styles={{ body: { paddingTop: 12, paddingBottom: 12 } }}>
      <FilterBar
        onReset={
          hasFilters
            ? () =>
                onChange({
                  dateRange: defaultDailyTrackingDateRange(),
                  segmentIds: [],
                  contactCategoryIds: [],
                  assigneeIds: [],
                })
            : undefined
        }
      >
        <>
          <Space wrap size={12} align="center">
            <Tooltip title="Select date range for daily task tracking">
              <span style={{ display: 'inline-block' }}>
                <RangePicker
                  value={value.dateRange}
                  onChange={(dates) => {
                    if (dates?.[0] && dates[1]) {
                      onChange({ ...value, dateRange: [dates[0], dates[1]] });
                    }
                  }}
                />
              </span>
            </Tooltip>
            <Divider type="vertical" style={{ height: 32, margin: 0 }} />
          </Space>
          <Select<string[]>
            mode="multiple"
            allowClear
            placeholder="Customer segments"
            options={segmentOptions}
            value={value.segmentIds}
            onChange={(segmentIds) => onChange({ ...value, segmentIds })}
            style={{ minWidth: 220, flex: '1 1 180px' }}
            optionFilterProp="label"
            maxTagCount="responsive"
          />
          <Select<number[]>
            mode="multiple"
            allowClear
            placeholder="Contact categories"
            options={categoryOptions}
            value={value.contactCategoryIds}
            onChange={(contactCategoryIds) => onChange({ ...value, contactCategoryIds })}
            style={{ minWidth: 220, flex: '1 1 180px' }}
            optionFilterProp="label"
            maxTagCount="responsive"
          />
          {canManage ? (
            <Select<string[]>
              mode="multiple"
              allowClear
              placeholder="Salespeople (empty = all)"
              options={assigneeOptions}
              value={value.assigneeIds}
              onChange={(assigneeIds) => onChange({ ...value, assigneeIds })}
              style={{ minWidth: 240, flex: '1 1 200px' }}
              optionFilterProp="label"
              maxTagCount="responsive"
            />
          ) : null}
        </>
      </FilterBar>
    </Card>
  );
};

export default DailyTaskTrackingFilterBar;
